import {
  Button,
  type ButtonProps as ButtonV3Props,
} from '@jouzen/ecom-components/v3/Button';
import {
  GridItem,
  type GridItemProps,
} from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import SectionHeading from '../SectionHeading';

interface ButtonProps extends Omit<ButtonV3Props, 'children'> {
  readonly label: MessageKey;
}

interface SectionHeaderProps {
  readonly button?: ButtonProps;
  readonly className?: string;
  readonly copy: {
    key: MessageKey;
    className?: string;
    colStart?: GridItemProps['colStart'];
    colEnd?: GridItemProps['colEnd'];
  };
  readonly title: {
    key: MessageKey;
    className?: string;
    colStart?: GridItemProps['colStart'];
    colEnd?: GridItemProps['colEnd'];
  };
}

const SectionHeader = ({
  button,
  className,
  copy,
  title,
}: SectionHeaderProps) => {
  const t = useTranslations();
  const { label, ...buttonProps } = button ?? {};

  const mergedTitleColEnd: GridItemProps['colEnd'] = {
    sm: 'main',
    md: 21,
    lg: 19,
    ...title.colEnd,
  };

  const mergedTitleColStart: GridItemProps['colStart'] = {
    sm: 'main',
    md: 5,
    lg: 7,
    ...title.colStart,
  };

  const mergedDescriptionColEnd: GridItemProps['colEnd'] = {
    sm: 'main',
    md: 21,
    lg: 19,
    ...copy.colEnd,
  };

  const mergedDescriptionColStart: GridItemProps['colStart'] = {
    sm: 'main',
    md: 5,
    lg: 7,
    ...copy.colStart,
  };
  return (
    <div className={className}>
      <LayoutGrid>
        <GridItem
          className={title.className ?? ''}
          colEnd={mergedTitleColEnd}
          colStart={mergedTitleColStart}
        >
          <SectionHeading
            animated
            content={title.key}
            emAnimationProps={{
              transition: { delay: 0.5, duration: 1, type: 'tween' },
              viewport: { once: true },
            }}
            typographyProps={{
              align: 'center',
              className: 'leading-tight tracking-tighter',
              color: 'inherit',
              Element: 'h3',
              variant: 'h2',
            }}
            wrapperAnimationProps={{
              initial: { filter: 'blur(5px)', opacity: 0 },
              transition: { delay: 0.2, duration: 0.75, type: 'tween' },
            }}
          />
        </GridItem>
        <GridItem
          className={copy.className ?? ''}
          colEnd={mergedDescriptionColEnd}
          colStart={mergedDescriptionColStart}
        >
          <Typography align="center" className="mt-6" variant="h6">
            {t(copy.key)}
          </Typography>
          {button && (
            <div className="flex justify-center">
              <Button className="mt-10" {...buttonProps}>
                {t(label!)}
              </Button>
            </div>
          )}
        </GridItem>
      </LayoutGrid>
    </div>
  );
};

export default SectionHeader;
